import { Stack } from '@/components/layout';
import { Loading } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import SessionHistoryEmptyState from '@/features/guardian-player/components/player-session-histories/session-history-empty-state';
import SessionHistoryListItem from '@/features/guardian-player/components/player-session-histories/session-history-list-item';
import SessionHistorySummary from '@/features/guardian-player/components/player-session-histories/session-history-summary';
import { PlayerSessionDetailContent } from '@/features/guardian-player/screens/player-session-detail-screen';
import {
  buildSessionHistoryListItems,
  formatSessionHistoryStartDate,
  type SessionHistoryListDataItem,
} from '@/features/guardian-player/components/player-session-histories/utils';
import {
  useGuardianPlayerSessionSummaryQuery,
  useGuardianPlayerSessionsQuery,
} from '@/features/guardian-player/queries';
import type { GuardianPlayerSession } from '@/features/guardian-player/services';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { guardianColors } from '@/theme/colors';
import { useRoute } from '@react-navigation/native';
import { FlashList, type ListRenderItem } from '@shopify/flash-list';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const strings = STRINGS.guardian.playerSessionHistories;

const PlayerSessionHistoriesScreen = () => {
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'PlayerSessionHistories'>['route']>();
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const { player } = route.params;
  const [selectedSessionId, setSelectedSessionId] = useState<string>();

  const firstName = player.firstname?.trim() || strings.fallbackPlayerName;

  const { data: summaryData } = useGuardianPlayerSessionSummaryQuery(player.id);
  const {
    data: sessionsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isSessionsLoading,
  } = useGuardianPlayerSessionsQuery(player.id);

  const sessions = useMemo(
    () => (sessionsData?.pages ?? []).flatMap(page => page.sessions),
    [sessionsData],
  );

  const listItems = useMemo(() => buildSessionHistoryListItems(sessions), [sessions]);
  const totalPins =
    summaryData?.totalPins ?? sessions.reduce((total, session) => total + session.pinCount, 0);
  const totalSessions = summaryData?.totalSessions ?? sessions.length;
  const startDate =
    formatSessionHistoryStartDate(summaryData?.startDate) ||
    formatSessionHistoryStartDate(sessions.at(-1)?.date);

  useEffect(() => {
    if (!isTablet) {
      setSelectedSessionId(undefined);
      return;
    }

    if (sessions.length === 0) {
      setSelectedSessionId(undefined);
      return;
    }

    const selectedSessionExists = sessions.some(session => session.id === selectedSessionId);
    if (!selectedSessionExists) {
      setSelectedSessionId(sessions[0].id);
    }
  }, [isTablet, selectedSessionId, sessions]);

  const handleEndReached = useCallback(() => {
    if (isSessionsLoading || !hasNextPage || isFetchingNextPage) return;

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isSessionsLoading]);

  const handleOpenSessionDetail = useCallback(
    (session: GuardianPlayerSession) => {
      if (isTablet) {
        setSelectedSessionId(session.id);
        return;
      }

      navigation.navigate('PlayerSessionDetail', { player, sessionId: session.id });
    },
    [isTablet, navigation, player],
  );

  const renderItem = useCallback<ListRenderItem<SessionHistoryListDataItem>>(
    ({ item }) => (
      <SessionHistoryListItem
        item={item}
        onOpenSessionDetail={handleOpenSessionDetail}
        selected={isTablet && item.kind === 'session' && item.session.id === selectedSessionId}
      />
    ),
    [handleOpenSessionDetail, isTablet, selectedSessionId],
  );

  const summary = useMemo(
    () => (
      <SessionHistorySummary
        sessionCount={totalSessions}
        totalPins={totalPins}
        startDate={startDate}
      />
    ),
    [startDate, totalPins, totalSessions],
  );

  const emptyComponent = useMemo(
    () =>
      isSessionsLoading ? (
        <Stack style={styles.loadingState}>
          <Loading />
        </Stack>
      ) : (
        <SessionHistoryEmptyState />
      ),
    [isSessionsLoading, styles.loadingState],
  );

  const footerComponent = useMemo(
    () =>
      isFetchingNextPage ? (
        <Stack style={styles.footerLoading}>
          <Loading />
        </Stack>
      ) : null,
    [isFetchingNextPage, styles.footerLoading],
  );

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <GuardianScreenHeader
        title={strings.screenTitle.replace('{name}', firstName)}
        onBack={navigation.goBack}
      />
      {isTablet ? (
        <Stack direction="row" flex={1} style={styles.tabletContent}>
          <Stack style={styles.tabletListPane}>
            <FlashList
              style={styles.list}
              data={listItems}
              renderItem={renderItem}
              keyExtractor={item => item.id}
              getItemType={item => item.kind}
              ListHeaderComponent={summary}
              ListEmptyComponent={emptyComponent}
              ListFooterComponent={footerComponent}
              onEndReached={handleEndReached}
              onEndReachedThreshold={0.4}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={[
                styles.tabletListContent,
                { paddingBottom: insets.bottom + scale(20) },
              ]}
            />
          </Stack>
          <Stack flex={1} style={styles.tabletDetailPane}>
            <PlayerSessionDetailContent
              playerId={player.id}
              sessionId={selectedSessionId}
              contentContainerStyle={styles.tabletDetailContent}
            />
          </Stack>
        </Stack>
      ) : (
        <FlashList
          style={styles.list}
          data={listItems}
          renderItem={renderItem}
          keyExtractor={item => item.id}
          getItemType={item => item.kind}
          ListHeaderComponent={summary}
          ListEmptyComponent={emptyComponent}
          ListFooterComponent={footerComponent}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, { paddingBottom: insets.bottom + scale(20) }]}
        />
      )}
    </Stack>
  );
};

const useStyles = createStyles(({ scale }) => ({
  listContent: {
    paddingHorizontal: scale(20),
  },
  list: {
    flex: 1,
  },
  tabletContent: {
    borderTopWidth: 1,
    borderTopColor: guardianColors['3'],
  },
  tabletListPane: {
    width: scale({ phone: 0, tablet: 390 }),
  },
  tabletListContent: {
    paddingHorizontal: scale(16),
  },
  tabletDetailPane: {
    borderLeftWidth: 1,
    borderLeftColor: guardianColors['3'],
  },
  tabletDetailContent: {
    paddingTop: scale(20),
    paddingHorizontal: scale(20),
  },
  loadingState: {
    paddingVertical: scale(64),
  },
  footerLoading: {
    paddingVertical: scale(16),
  },
}));

export default PlayerSessionHistoriesScreen;
